import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Hash, Loader2, Lock, Paperclip, Plus, Radio, Send, X } from "lucide-react";
import api, { errMsg, getToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Chip, Empty, Err } from "@/components/community/kit";
import { allows, socketUrl } from "@/lib/community";
import { Message } from "@/components/community/Message";

const TYPING_EVERY = 4000;

export const Chat = ({ community }) => {
  const cid = community.id;
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [channels, setChannels] = useState([]);
  const [active, setActive] = useState(params.get("channel") || "");
  const [messages, setMessages] = useState([]);
  const [typing, setTyping] = useState([]);
  const [canPost, setCanPost] = useState(true);
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [roster, setRoster] = useState([]);
  const [mentions, setMentions] = useState([]);
  const [picker, setPicker] = useState("");
  const [live, setLive] = useState(false);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [newChannel, setNewChannel] = useState("");
  const lastAt = useRef("");
  const loadRef = useRef(null);
  const typedAt = useRef(0);
  const endRef = useRef(null);
  const fileRef = useRef(null);

  const canModerate = allows(community, "moderate", "full") || community.platform_admin;
  const canChannels = canModerate;

  const loadChannels = useCallback(async () => {
    const { data } = await api.get(`/communities/${cid}/channels`);
    const open = data.channels.filter((c) => !c.archived);
    setChannels(open);
    setActive((current) => (current && open.some((c) => c.id === current) ? current
      : open[0]?.id || ""));
  }, [cid]);

  const loadMessages = useCallback(async (incremental = false) => {
    if (!active) return;
    const paramsIn = { limit: 60 };
    if (incremental && lastAt.current) paramsIn.after = lastAt.current;
    try {
      const { data } = await api.get(`/communities/${cid}/channels/${active}/messages`,
        { params: paramsIn });
      setTyping(data.typing || []);
      setCanPost(data.can_post);
      if (incremental) {
        // The send and the poll can both bring back the same message — key on the id, not the
        // order they arrive in, or it renders twice.
        if (data.messages.length) {
          setMessages((current) => {
            const seen = new Set(current.map((m) => m.id));
            const fresh = data.messages.filter((m) => !seen.has(m.id));
            return fresh.length ? [...current, ...fresh] : current;
          });
        }
      } else {
        setMessages(data.messages);
      }
      const newest = data.messages[data.messages.length - 1]?.at;
      if (newest && newest > lastAt.current) lastAt.current = newest;
    } catch (e) { setError(errMsg(e)); }
  }, [cid, active]);

  loadRef.current = loadMessages;

  useEffect(() => {
    loadChannels().catch((e) => setError(errMsg(e)));
    api.get(`/communities/${cid}/roster`).then(({ data }) => setRoster(data.members)).catch(() => {});
  }, [cid, loadChannels]);

  useEffect(() => {
    if (!active) return;
    lastAt.current = "";
    setMessages([]);
    loadMessages(false);
    api.post(`/communities/${cid}/channels/${active}/read`).then(loadChannels).catch(() => {});
    const next = new URLSearchParams(params);
    next.set("channel", active);
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // Live socket first; the poll below keeps working if the upgrade never completes.
  useEffect(() => {
    let socket;
    try {
      socket = new WebSocket(socketUrl(cid, getToken()));
    } catch { return undefined; }
    socket.onopen = () => setLive(true);
    socket.onclose = () => setLive(false);
    socket.onerror = () => setLive(false);
    socket.onmessage = (event) => {
      let payload;
      try { payload = JSON.parse(event.data); } catch { return; }
      if (payload.kind === "typing" && payload.name) {
        setTyping((t) => (t.includes(payload.name) ? t : [...t, payload.name]));
      } else {
        loadRef.current?.(true);
      }
    };
    return () => socket.close();
  }, [cid]);

  useEffect(() => {
    const timer = setInterval(() => loadRef.current?.(true), live ? 10000 : 3000);
    return () => clearInterval(timer);
  }, [live, active]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [messages.length]);

  const onType = (value) => {
    setText(value);
    const at = value.lastIndexOf("@");
    setPicker(at >= 0 && !/\s/.test(value.slice(at + 1)) ? value.slice(at + 1).toLowerCase() : "");
    if (value && Date.now() - typedAt.current > TYPING_EVERY && canPost) {
      typedAt.current = Date.now();
      api.post(`/communities/${cid}/channels/${active}/typing`).catch(() => {});
    }
  };

  const insertMention = (person) => {
    const at = text.lastIndexOf("@");
    setText(`${text.slice(0, at)}@${person.name} `);
    setMentions((m) => (m.includes(person.user_id) ? m : [...m, person.user_id]));
    setPicker("");
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy("upload"); setError("");
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(`/communities/${cid}/attachments`, form);
      setFiles((f) => [...f, data.attachment]);
    } catch (e) { setError(errMsg(e)); } finally { setBusy(""); }
  };

  const send = async () => {
    if (!text.trim() && files.length === 0) return;
    setBusy("send"); setError("");
    try {
      await api.post(`/communities/${cid}/channels/${active}/messages`,
        { body: text, attachments: files, mentions });
      setText(""); setFiles([]); setMentions([]); setPicker("");
      await loadMessages(true);
    } catch (e) { setError(errMsg(e)); } finally { setBusy(""); }
  };

  const createChannel = async () => {
    setBusy("channel"); setError("");
    try {
      const { data } = await api.post(`/communities/${cid}/channels`, { name: newChannel });
      setNewChannel("");
      await loadChannels();
      setActive(data.channel.id);
    } catch (e) { setError(errMsg(e)); } finally { setBusy(""); }
  };

  const channel = channels.find((c) => c.id === active);
  const suggestions = picker
    ? roster.filter((p) => p.name.toLowerCase().includes(picker)).slice(0, 6)
    : [];

  return (
    <div data-testid="community-chat" className="space-y-4">
      <div data-testid="chat-channels" className="-mx-1 flex items-center gap-2 overflow-x-auto px-1 pb-1">
        {channels.map((c) => (
          <button key={c.id} data-testid={`chat-channel-${c.key}`} onClick={() => setActive(c.id)}
            className={`flex shrink-0 items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs transition-colors ${
              active === c.id ? "border-gold/40 bg-gold/10 text-gold"
                : "border-ink-600 text-slate-400 hover:text-slate-100"}`}>
            {c.write === "admins" ? <Lock className="h-3 w-3" /> : <Hash className="h-3 w-3" />}
            {c.name}
            {c.unread > 0 && (
              <span data-testid={`chat-unread-${c.key}`}
                className="ml-1 rounded-full bg-gold px-1.5 text-[10px] font-semibold text-ink-900">
                {c.unread}
              </span>
            )}
          </button>
        ))}
        {live && (
          <Chip testId="chat-live" className="border-emerald-500/40 bg-emerald-500/10 text-emerald-300">
            <Radio className="mr-1 h-3 w-3 animate-pulse" /> live
          </Chip>
        )}
      </div>

      {canChannels && (
        <div className="flex flex-col gap-2 sm:flex-row">
          <Input data-testid="chat-new-channel" value={newChannel} placeholder="New channel name"
            onChange={(e) => setNewChannel(e.target.value)} className="sm:max-w-xs" />
          <Button data-testid="chat-new-channel-btn" size="sm" variant="outline"
            onClick={createChannel} disabled={newChannel.trim().length < 2 || busy === "channel"}>
            <Plus className="mr-1.5 h-3.5 w-3.5" /> Add channel
          </Button>
        </div>
      )}

      <Err testId="chat-error">{error}</Err>

      <div className="rounded-2xl border border-ink-600 bg-ink-800">
        <div className="border-b border-ink-600 px-5 py-3">
          <p className="text-sm text-slate-200">
            {channel?.write === "admins" ? <Lock className="mr-1.5 inline h-3.5 w-3.5 text-gold" /> : "#"} {channel?.name || "Channel"}
          </p>
          {channel?.description && <p className="mt-0.5 text-xs text-slate-500">{channel.description}</p>}
        </div>

        <div data-testid="chat-messages" className="max-h-[52vh] min-h-[280px] overflow-y-auto px-5">
          {messages.length === 0 ? (
            <div className="py-10">
              <Empty testId="chat-empty">Nothing here yet — say hello.</Empty>
            </div>
          ) : (
            <ul className="divide-y divide-ink-700/60">
              {messages.map((m) => (
                <Message key={m.id} message={m} communityId={cid} channelId={active}
                  mine={m.user_id === String(user?.id || "")} canModerate={canModerate}
                  canPost={canPost}
                  onChanged={() => { lastAt.current = ""; loadMessages(false); }} />
              ))}
            </ul>
          )}
          <div ref={endRef} />
        </div>

        <div className="border-t border-ink-600 px-5 py-3">
          {typing.length > 0 && (
            <p data-testid="chat-typing" className="mb-2 font-mono text-[10px] uppercase tracking-widest text-gold">
              {typing.slice(0, 3).join(", ")} {typing.length === 1 ? "is" : "are"} typing…
            </p>
          )}
          {files.length > 0 && (
            <div data-testid="chat-pending-files" className="mb-2 flex flex-wrap gap-2">
              {files.map((f, i) => (
                <span key={i} className="inline-flex items-center gap-1.5 rounded-full border border-ink-600 bg-ink-900 px-2.5 py-1 text-[11px] text-slate-300">
                  {f.name}
                  <button data-testid={`chat-file-remove-${i}`}
                    onClick={() => setFiles(files.filter((_, j) => j !== i))}>
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
          {suggestions.length > 0 && (
            <div data-testid="chat-mention-picker" className="mb-2 flex flex-wrap gap-2">
              {suggestions.map((p) => (
                <button key={p.user_id} data-testid={`chat-mention-${p.user_id}`}
                  onClick={() => insertMention(p)}
                  className="rounded-full border border-gold/30 bg-gold/10 px-2.5 py-1 text-[11px] text-gold">
                  @{p.name}
                </button>
              ))}
            </div>
          )}
          {canPost ? (
            <div className="flex items-end gap-2">
              <input ref={fileRef} data-testid="chat-file-input" type="file" className="hidden"
                onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
              <button data-testid="chat-attach" onClick={() => fileRef.current?.click()}
                className="flex h-10 w-10 items-center justify-center rounded-md border border-ink-600 text-slate-400 hover:text-gold">
                {busy === "upload" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
              </button>
              <textarea data-testid="chat-input" value={text} rows={1}
                onChange={(e) => onType(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
                }}
                placeholder="Message your community — @ to mention someone"
                className="max-h-32 min-h-10 min-w-0 flex-1 resize-y rounded-md border border-ink-600 bg-ink-900 px-3 py-2.5 text-sm text-slate-200 placeholder:text-slate-600 focus:border-gold/40 focus:outline-none" />
              <button data-testid="chat-send" onClick={send}
                disabled={busy === "send" || (!text.trim() && files.length === 0)}
                className="flex h-10 w-10 items-center justify-center rounded-md border border-gold/30 bg-gold/10 text-gold transition-colors hover:bg-gold/20 disabled:opacity-40">
                {busy === "send" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </div>
          ) : (
            <p data-testid="chat-readonly" className="text-xs text-slate-500">
              {channel?.write === "admins"
                ? "Only leadership posts in this channel."
                : "You cannot post here right now."}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
